/**
 * product-swatch.js
 * Works for both collection card swatches and product detail swatches.
 *
 * - Sets --swatch-background (so visuals match product page)
 * - Collection: hover/click change card image
 * - Product page: clicking a swatch selects the matching variant option input (so theme updates main image)
 *
 * Install:
 * 1) Upload as assets/product-swatch.js
 * 2) Add <script defer src="{{ 'product-swatch.js' | asset_url }}"></script> just before </body>
 * 3) Ensure CSS from earlier is present (uses --swatch-background).
 */

(function () {
  // mapping: friendly color names -> hex (tweak for perfect match)
  var COLOR_MAP = {
    'white gold': '#ffffff',
    'yellow gold': '#ffd400',
    'rose gold': '#c77b79',
    'gold': '#d4af37',
    'black': '#000000',
    'silver': '#c0c0c0',
    'white': '#ffffff'
  };

  function resolveColorValue(colorText) {
    if (!colorText) return null;
    var txt = colorText.trim();

    if (/^#([0-9A-F]{3}){1,2}$/i.test(txt)) return txt;
    if (/^rgba?\(/i.test(txt)) return txt;

    var mapped = COLOR_MAP[txt.toLowerCase()];
    if (mapped) return mapped;

    // deterministic fallback
    var seed = 0;
    for (var i = 0; i < txt.length; i++) seed = (seed << 5) - seed + txt.charCodeAt(i);
    var hue = Math.abs(seed) % 360;
    return 'hsl(' + hue + ',60%,65%)';
  }

  function initCollectionSwatches() {
    var productCards = document.querySelectorAll('.card-wrapper.product-card-wrapper');
    if (!productCards || !productCards.length) return;

    productCards.forEach(function (card) {
      var swatchContainer = card.querySelector('.card__swatches');
      if (!swatchContainer) return;

      var img = card.querySelector('.card__media img') || card.querySelector('img');
      if (!img) return;
      var defaultSrc = img.getAttribute('src') || img.dataset.src || img.src;

      var swatches = Array.prototype.slice.call(swatchContainer.querySelectorAll('.swatch'));
      if (!swatches.length) return;

      // Apply visual color
      swatches.forEach(function (btn) {
        var colorText = (btn.dataset.color || btn.getAttribute('title') || '').trim();
        var visual = btn.querySelector('.swatch__visual');
        var colorVal = resolveColorValue(colorText);
        if (visual) {
          visual.style.setProperty('--swatch-background', colorVal);
          visual.style.background = colorVal; // fallback
        }
      });

      var active = null;
      var canHover = window.matchMedia && window.matchMedia('(hover: hover)').matches;

      swatches.forEach(function (btn) {
        var variantImage = btn.dataset.image;
        if (!variantImage) return;

        if (canHover) {
          btn.addEventListener('mouseenter', function () {
            if (active) return;
            img.src = variantImage;
          });
          btn.addEventListener('mouseleave', function () {
            if (active) return;
            img.src = defaultSrc;
          });
        }

        btn.addEventListener('click', function (e) {
          e.preventDefault();
          if (active === btn) {
            active = null;
            img.src = defaultSrc;
            btn.classList.remove('swatch--active');
          } else {
            active = btn;
            img.src = variantImage;
            swatches.forEach(function (s) { s.classList.remove('swatch--active'); });
            btn.classList.add('swatch--active');
          }
        });

        btn.addEventListener('focus', function () {
          if (active) return;
          img.src = variantImage;
        });
        btn.addEventListener('blur', function () {
          if (active) return;
          img.src = defaultSrc;
        });
      });
    });
  }

  function initProductPageSwatches() {
    // product template swatches typically live in a product form area
    // we'll find any swatch buttons/labels inside the product form area
    var productForms = document.querySelectorAll('form[action^="/cart"], form.product-form, .product__form, [data-product-form]');

    productForms.forEach(function (form) {
      // find swatches inside the form (some themes render them as .swatch too)
      var swatches = form.querySelectorAll('.swatch, .swatch-input_label, .swatch-label');
      if (!swatches || swatches.length === 0) {
        // fallback: search for the product option inputs (radio/label pairs) and convert them visually
        swatches = form.querySelectorAll('label.swatch-input_label, label[for^="template-"]');
      }
      if (!swatches || swatches.length === 0) return;

      // Ensure clicking a swatch selects the corresponding input option (value match)
      swatches.forEach(function (sw) {
        // Attempt to read color text from data attributes or visible text
        var colorText = sw.dataset.color || sw.getAttribute('title') || (sw.querySelector('.swatch__visual') && sw.querySelector('.swatch__visual').textContent) || sw.textContent || '';
        var colorVal = resolveColorValue(colorText.trim());

        // Apply visual background same as product page
        var visual = sw.querySelector('.swatch__visual') || sw.querySelector('.swatch') || sw;
        if (visual) {
          visual.style.setProperty('--swatch-background', colorVal);
          visual.style.background = colorVal;
        }

        // On product page, clicking swatch should select product option input with matching option value
        sw.addEventListener('click', function (e) {
          // If sw has data-variant-id and theme supports direct variant switching, try to use it
          var variantId = sw.dataset.variantId || sw.getAttribute('data-variant-id') || null;
          if (variantId) {
            // If there's an input matching the variant id, check it
            var variantInput = form.querySelector('input[value="' + variantId + '"], input[data-variant-id="' + variantId + '"]');
            if (variantInput) {
              variantInput.checked = true;
              variantInput.dispatchEvent(new Event('change', { bubbles: true }));
              return;
            }
          }

          // Otherwise, match by option value text
          // Find any select/radio that holds option values
          var targetValue = (sw.dataset.color || sw.getAttribute('data-option-value') || sw.getAttribute('title') || sw.textContent || '').trim();
          if (!targetValue) return;

          // First try radio inputs with data-option-value or value exact match
          var optInput = form.querySelector('input[data-option-value="' + CSS.escape(targetValue) + '"], input[value="' + CSS.escape(targetValue) + '"]');
          if (optInput) {
            optInput.checked = true;
            optInput.dispatchEvent(new Event('change', { bubbles: true }));
            return;
          }

          // Next: try select elements (option matching)
          var selects = form.querySelectorAll('select');
          if (selects.length) {
            for (var i = 0; i < selects.length; i++) {
              var sel = selects[i];
              var opt = sel.querySelector('option[value="' + CSS.escape(targetValue) + '"]');
              if (opt) {
                sel.value = opt.value;
                sel.dispatchEvent(new Event('change', { bubbles: true }));
                return;
              }
            }
          }

          // As a last fallback: try to find radio inputs by checking labels that contain the same text
          var labels = form.querySelectorAll('label');
          for (var j = 0; j < labels.length; j++) {
            var lbl = labels[j];
            if (lbl.textContent && lbl.textContent.trim() === targetValue) {
              var forId = lbl.getAttribute('for');
              if (forId) {
                var input = form.querySelector('#' + CSS.escape(forId));
                if (input) {
                  input.checked = true;
                  input.dispatchEvent(new Event('change', { bubbles: true }));
                  return;
                }
              }
            }
          }
        });
      });
    });
  }

  function initAll() {
    initCollectionSwatches();
    initProductPageSwatches();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initAll);
  } else {
    initAll();
  }

  document.addEventListener('shopify:section:load', initAll);
  document.addEventListener('shopify:section:reorder', initAll);
  document.addEventListener('shopify:section:select', initAll);
})();
document.addEventListener('DOMContentLoaded', function () {
  const container = document.querySelector('.product-toggles');
  if (!container) return;

  container.querySelectorAll('.pw-accordion-button').forEach(button => {
    button.addEventListener('click', function () {
      const expanded = this.getAttribute('aria-expanded') === 'true';
      const contentId = this.getAttribute('aria-controls');
      const content = document.getElementById(contentId);

      this.setAttribute('aria-expanded', (!expanded).toString());
      content.hidden = expanded;
    });
  });
});
